import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, checkAuth } = useAuth();
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;

    const checkStatus = async () => {
      try {
        const res = await fetch(`${API}/subscription/status/${sessionId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'completed' || data.status === 'paid') {
            setStatus('success');
            toast.success('Subskrypcja aktywowana!');
            await checkAuth();
            return true;
          }
        }
        return false;
      } catch (error) {
        return false;
      }
    };

    const poll = async () => {
      const done = await checkStatus();
      if (!done && attempts < maxAttempts) {
        attempts++;
        setTimeout(poll, 2000);
      } else if (!done) {
        setStatus('failed');
      }
    };

    poll();
  }, [searchParams, token, checkAuth]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="subscription-success-page">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 text-orange-500 animate-spin mx-auto mb-6" />
              <h2 className="font-heading font-bold text-xl mb-2">Weryfikacja płatności</h2>
              <p className="text-slate-600">Proszę czekać...</p>
            </>
          )}
          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="font-heading font-bold text-2xl mb-2 text-green-600">Subskrypcja aktywna!</h2>
              <p className="text-slate-600 mb-6">Twoje konto firmowe ma teraz pełny dostęp do platformy.</p>
              <div className="space-y-3">
                <Button onClick={() => navigate('/dashboard/company')} className="w-full bg-orange-500 hover:bg-orange-600">Przejdź do panelu</Button>
                <Button onClick={() => navigate('/offers')} variant="outline" className="w-full">Przeglądaj ogłoszenia</Button>
              </div>
            </>
          )}
          {status === 'failed' && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="font-heading font-bold text-2xl mb-2 text-red-600">Płatność nieudana</h2>
              <p className="text-slate-600 mb-6">Wystąpił problem z płatnością. Spróbuj ponownie.</p>
              <div className="space-y-3">
                <Button onClick={() => navigate('/subscription')} className="w-full bg-orange-500 hover:bg-orange-600">Spróbuj ponownie</Button>
                <Button onClick={() => navigate('/dashboard/company')} variant="outline" className="w-full">Wróć do panelu</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
